"use client";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { ThemeColors, ThemeService } from "@/services/themeService";
import type { RootState } from "@/store/store";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (theme: "light" | "dark") => void;
  themeColors: ThemeColors | null;
  setThemeColors: (colors: ThemeColors) => void;
  loadTheme: () => Promise<void>;
  isThemeLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [isDark, setIsDark] = useState(false);
  const [themeColors, setThemeColors] = useState<ThemeColors | null>(null);
  const [isThemeLoading, setIsThemeLoading] = useState(false);
  const { currentStoreId, isAuthenticated, hasAuthorizedStore } = useSelector(
    (state: RootState) => state.auth,
  );
  const canLoadStoreTheme =
    isAuthenticated && hasAuthorizedStore && UUID_RE.test(currentStoreId || "");

  const loadTheme = useCallback(async () => {
    if (!canLoadStoreTheme) {
      setThemeColors(null);
      setIsThemeLoading(false);
      return;
    }

    setIsThemeLoading(true);
    try {
      const theme = await ThemeService.getTheme();
      setThemeColors(theme);
    } catch (error) {
      console.error("Failed to load theme colors:", error);
      setThemeColors(null);
    } finally {
      setIsThemeLoading(false);
    }
  }, [canLoadStoreTheme]);

  useEffect(() => {
    // Check if dark mode is enabled in localStorage
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    const initialTheme = savedTheme === "dark" || (!savedTheme && prefersDark);

    setIsDark(initialTheme);
    document.documentElement.classList.toggle("dark", initialTheme);

  }, []);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  const setTheme = (theme: "light" | "dark") => {
    const newTheme = theme === "dark";
    setIsDark(newTheme);
    document.documentElement.classList.toggle("dark", newTheme);
    localStorage.setItem("theme", theme);
  };

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    document.documentElement.classList.toggle("dark", newTheme);
    localStorage.setItem("theme", newTheme ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider
      value={{
        isDark,
        toggleTheme,
        setTheme,
        themeColors,
        setThemeColors,
        loadTheme,
        isThemeLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
